import moment from 'moment';
import 'moment-timezone';
import { timestampToDate } from './dates';
import { UserType } from './enums';

export const isArrayLength = arr => {
  // Check if the input parameter is an array and has a length greater than zero.
  return Array.isArray(arr) && (arr.length > 0 ?? false);
};

export const normalizedStatus = status => status?.trim().toLowerCase();

function capitalize(str) {
  return str
    ? str
        .toString()
        .toLowerCase()
        .replace(/\b\w/g, char => char.toUpperCase())
    : '';
}

export function formatSlabTitle(publicData, title, currentUser) {
  const { wood_species, wood_species_other, slab_number, log_number } = publicData;

  const species =
    wood_species && wood_species.toLowerCase() === 'other' ? wood_species_other : wood_species;

  const slabNum = slab_number ? ` ${slab_number}` : '';
  const logNum = log_number ? `-${log_number}` : '';
  const sellerName = currentUser?.attributes?.profile?.displayName || 'Seller';

  const reformedTitle = `${capitalize(species)} ${title} -${slabNum} ${logNum} from ${capitalize(
    sellerName
  )}`;

  return reformedTitle.trim();
}

export const getReformedTitle = l => {
  return l?.attributes?.publicData?.reformedTitle || l?.attributes?.title;
};

export function retrieveUserFavorites(user) {
  if (!user || !user.id) {
    return null;
  }
  const protectedData = user.attributes?.profile?.protectedData || {};
  return protectedData.favorites ?? [];
}

export const isWindowDefined = () => {
  return typeof window !== 'undefined';
};

export const cardRenderSizes = isMapVariant => {
  if (isMapVariant) {
    // Panel width relative to the viewport
    const panelMediumWidth = 50;
    const panelLargeWidth = 62.5;
    return [
      '(max-width: 767px) 100vw',
      `(max-width: 1023px) ${panelMediumWidth}vw`,
      `(max-width: 1920px) ${panelLargeWidth / 2}vw`,
      `${panelLargeWidth / 3}vw`,
    ].join(', ');
  } else {
    return [
      '(max-width: 549px) 100vw',
      '(max-width: 767px) 50vw',
      `(max-width: 1439px) 26vw`,
      `(max-width: 1920px) 18vw`,
      `14vw`,
    ].join(', ');
  }
};

export const extractRescheduleBookingDetails = transaction => {
  const rescheduleBookingParams = transaction?.attributes?.metadata?.rescheduleBookingParams || [];
  const transitions = transaction?.attributes?.transitions || [];

  // Filter out the "Reschedule-booking" transitions
  const rescheduleBookingTransitions = transitions.filter(
    transition => transition.transition === 'transition/reschedule-booking'
  );

  if (rescheduleBookingTransitions.length === 0) {
    return null; // Return null if no Reschedule-booking transitions are found
  }

  // Get the latest "Reschedule-booking" transition
  const latestRescheduleBooking =
    rescheduleBookingTransitions[rescheduleBookingTransitions.length - 1];

  // Map over the booking details to find the matching one with the latest transition
  const latestMatchedBookingDetail = rescheduleBookingParams
    .map((detail, index) => {
      // Check if the detail's createdDate matches the latest transition's createdAt (by minute)
      const isMatched = moment(detail.createdDate, 'YYYY-MM-DD HH:mm:ss').isSame(
        moment(latestRescheduleBooking?.createdAt),
        'minute'
      );

      if (isMatched) {
        const rescheduleStartUnix = detail.rescheduleStartUnix;
        const rescheduleEndUnix = detail.rescheduleEndUnix;
        const createdDate = detail.createdDate;
        const status = detail?.status;
        const formattedStartDate = rescheduleStartUnix && timestampToDate(rescheduleStartUnix);
        const formattedEndDate = rescheduleEndUnix && timestampToDate(rescheduleEndUnix);
        const start = rescheduleStartUnix ? moment(rescheduleStartUnix).format('MMM D') : null;
        const end = rescheduleEndUnix ? moment(rescheduleEndUnix).format('MMM D') : null;

        return {
          start,
          end,
          transitionDetails: latestRescheduleBooking,
          createdDate,
          status,
          formattedStartDate,
          formattedEndDate,
        };
      }

      return null; // Skip unmatched entries
    })
    .filter(Boolean); // Remove nulls (unmatched entries)

  // Return the latest matched booking detail (if any)
  return latestMatchedBookingDetail[latestMatchedBookingDetail.length - 1] || null;
};

export const isBookingEnded = booking => {
  if (!booking || !booking.attributes || !booking.attributes.end) {
    return false; // If booking or end time is missing, treat as not ended
  }

  const bookingEndTime = new Date(booking.attributes.end).getTime();
  const currentTime = Date.now();

  return bookingEndTime < currentTime;
};

export const getRescheduleBookingDetails = transaction => {
  const rescheduleBookingParams = transaction?.attributes?.metadata?.rescheduleBookingParams || [];
  const transitions = transaction?.attributes?.transitions || [];
  // Filter out the "Reschedule-booking" transitions
  const rescheduleBookingTransitions = transitions.filter(
    transition => transition.transition === 'transition/reschedule-booking'
  );

  let lastEndDate = null; // To track the last end date and use it for the next start date

  // Create an array of objects containing formattedStartDate, formattedEndDate, and paymentIntent for each "Reschedule-booking"
  const result = rescheduleBookingTransitions.map((transition, index) => {
    const rescheduleBookingDetails = rescheduleBookingParams[index] || {};
    const rescheduleStartUnix = rescheduleBookingDetails.rescheduleStartUnix;
    const rescheduleEndUnix = rescheduleBookingDetails.rescheduleEndUnix;
    const status = rescheduleBookingDetails?.status;

    // If there is a lastEndDate, use it for the new start date; otherwise, use the current extendedStartUnix
    const start = rescheduleStartUnix
      ? moment(rescheduleStartUnix).format('MMM D')
      : rescheduleStartUnix
      ? moment(rescheduleStartUnix).format('MMM D')
      : null;
    const end = rescheduleEndUnix ? moment(rescheduleEndUnix).format('MMM D') : null;

    // Update lastEndDate to the current rescheduleEndUnix only if the status is 'accepted' or 'requested'
    if (status === 'accepted' || status === 'requested') {
      lastEndDate = rescheduleEndUnix ? moment(rescheduleEndUnix) : null;
    }

    // Set newStartDate only if the status is 'accepted' or 'requested'
    const newStartDate =
      (status === 'accepted' || status === 'requested') && end
        ? moment(rescheduleEndUnix).format('MMM D')
        : null;
    const createdDate = rescheduleBookingDetails.createdDate;

    return {
      start,
      end,
      transitionDetails: transition,
      createdDate,
      status,
      newStartDate,
    };
  });

  return result;
};

export const validateDuration = (
  startTimestamp,
  endTimestamp,
  originalDurationInHours,
  setDurationError
) => {
  if (!startTimestamp || !endTimestamp) {
    setDurationError(null);
    return;
  }

  const start = moment(Number(startTimestamp));
  const end = moment(Number(endTimestamp));

  const newDuration = end.diff(start, 'hours', true); // true = decimal precision

  if (Math.round(newDuration) !== originalDurationInHours) {
    setDurationError(
      `Please select exactly ${originalDurationInHours} hour(s) as originally booked!`
    );
  } else {
    setDurationError(null);
  }
};

export const getOriginalDurationInHours = transaction => {
  if (!transaction?.booking?.attributes?.start || !transaction?.booking?.attributes?.end) {
    return null;
  }

  const start = moment(transaction.booking.attributes.start);
  const end = moment(transaction.booking.attributes.end);

  const durationInHours = end.diff(start, 'hours'); // only full hours
  return durationInHours;
};

export const getTitleFromError = error => {
  if (error && error.apiErrors && Array.isArray(error.apiErrors) && error.apiErrors.length > 0) {
    const firstError = error.apiErrors[0];
    return firstError.title;
  }
  return null;
};

export const formatDateRangeForTimezone = dates => {
  if (!dates || (!dates.startDate && !dates.endDate)) return null;

  const { startDate, endDate } = dates;

  // Get the user's current timezone dynamically
  const timeZone = moment.tz.guess();

  // Function to format a date in the specified timezone
  const formatDate = date => {
    if (!date) return null;
    // Convert the date to the user's timezone and format it
    const zonedDate = moment(date).tz(timeZone);
    return zonedDate.format('YYYY-MM-DD');
  };

  const dateRange = [startDate ? formatDate(startDate) : null, endDate ? formatDate(endDate) : null]
    .filter(Boolean)
    .join(',');

  return dateRange || null;
};

export const getInitialImage = listing => {
  if (!listing || !listing?.id) return null;

  return listing?.images?.[0]?.attributes?.variants?.['square-small']?.url || '';
};

export function getSquareSmallImageUrl(user) {
  try {
    return user?.profileImage?.attributes?.variants?.['square-small']?.url || null;
  } catch (error) {
    console.error('Error extracting square-small image URL:', error);
    return null;
  }
}

export function mergeAllTimeSlots(monthlyTimeSlots) {
  let mergedTimeSlots = [];

  // Loop through each month in the monthlyTimeSlots object
  for (const month in monthlyTimeSlots) {
    // Check if the month has timeSlots and it's an array
    if (Array.isArray(monthlyTimeSlots[month].timeSlots)) {
      // Concatenate the timeSlots array into the mergedTimeSlots array
      mergedTimeSlots = mergedTimeSlots.concat(monthlyTimeSlots[month].timeSlots);
    }
  }

  return mergedTimeSlots;
}

export function checkAvailableSlots(start, endDate, timeSlotsOnSelectedMonth = []) {
  // If either start or end date is not provided, return "no dates"
  if (!start || !endDate) {
    return 'no dates';
  }

  // Parse start and end dates to moment objects (full days)
  const startMoment = moment(start).add(1, 'day');
  const endMoment = moment(endDate).endOf('day'); // Ensure full day coverage for end date

  // Split the provided date range into separate ranges
  const result = splitDateRanges(startMoment, endMoment);
  // Ensure both start and end of every range in result are strictly within an available slot
  const isAvailable = result.every(range => {
    const rangeStart = moment(range.start).startOf('day');
    const rangeEnd = moment(range.end).endOf('day');

    // Check if the rangeStart and rangeEnd fall strictly within one of the slots
    const matchingSlot = timeSlotsOnSelectedMonth.some(slot => {
      const slotStart = moment(slot.attributes.start).startOf('day');
      const slotEnd = moment(slot.attributes.end).endOf('day');

      // The selected range must start after or at the start of the slot
      const isStartWithinSlot = rangeStart.isSameOrAfter(slotStart);
      // The selected range must end strictly before the end of the slot
      const isEndWithinSlot = rangeEnd.isSameOrBefore(slotEnd);

      // Ensure both conditions are true
      return isStartWithinSlot && isEndWithinSlot;
    });

    // If there's no matching slot that covers the entire range, return false
    if (!matchingSlot) {
      return false;
    }

    return true; // Slot fully covers the range
  });

  return isAvailable ? true : false;
}

export const convertPriceToFixedPrice = price => {
  if (isNaN(price) || price === null || price === undefined) {
    return '$0.00';
  }

  const formattedPrice = (price / 100).toFixed(2);
  return `$${formattedPrice}`;
};

export function getFilteredLineItems(transaction) {
  const { lineItems } = transaction.attributes;
  const filteredItems = lineItems.filter(
    item =>
      item.code !== 'line-item/night' &&
      item.code !== 'line-item/security-deposit' &&
      item.code !== 'line-item/provider-commission' &&
      item.code !== 'line-item/customer-commission'
  );

  return filteredItems.map(item => {
    return {
      code: item.code.replace('line-item/', ''),
      amount: item.unitPrice.amount,
    };
  });
}
/**
 * Returns a capitalized string.
 * @param {string} str - The string to capitalize.
 * @returns {string|null} The capitalized string, or null if input is invalid.
 */
export const capitalizeFirstLetter = str => {
  if (typeof str !== 'string' || str.length === 0) {
    return null;
  }

  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const getFullEndDateTime = dateTimeString => {
  if (!dateTimeString) return '';

  const [startDateTime = '', endTime = ''] = dateTimeString.split(' - ') || [];
  const datePart = startDateTime.split(',')[0];

  return datePart && endTime ? `${datePart}, ${endTime}` : endTime;
};

export const isInstantBooking = l => {
  const instantBooking = l?.attributes?.publicData?.instantBooking;
  return instantBooking === 'true';
};
export const isQuoteTransaction = tx => {
  const isQuote = tx?.attributes?.protectedData?.isQuote;
  return isQuote
};

export const isQuoteBooking = l => {
  const callForQuote = l?.attributes?.publicData?.callForQuote;
  return Array.isArray(callForQuote) && callForQuote.includes('true');
};
function calculateDistance(lat1, lng1, lat2, lng2) {
  const R = 6371e3; // Earth's radius in meters
  const φ1 = degToRad(lat1);
  const φ2 = degToRad(lat2);
  const Δφ = φ2 - φ1;
  const λ1 = degToRad(lng1);
  const λ2 = degToRad(lng2);
  const Δλ = λ2 - λ1;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  const distance = R * c;

  return distance;
}

function degToRad(degrees) {
  return degrees * (Math.PI / 180);
}

export const obfuscateLocation = (center, scale = 2, minDistance = 4046.72) => {
  // Base degree of obfuscation (increased for greater distance)
  const baseVariance = 0.01;

  // Apply scaling factor within a wider controlled range
  const scaledVariance = Math.max(0.01, Math.min(0.03, baseVariance * scale)); // Wider range

  // Generate random offsets for latitude and longitude
  const randomLatVariance = Math.random() * (scaledVariance * 2) - scaledVariance;
  const randomLngVariance = Math.random() * (scaledVariance * 2) - scaledVariance;

  // Apply offsets to create initial obfuscated coordinates
  let newLat = center.lat + randomLatVariance;
  let newLng = center.lng + randomLngVariance;

  // Ensure minimum distance from actual location
  let distance = calculateDistance(center.lat, center.lng, newLat, newLng); // Assuming a distance calculation function
  while (distance < minDistance) {
    newLat += (Math.random() - 0.5) * scaledVariance;
    newLng += (Math.random() - 0.5) * scaledVariance;
    distance = calculateDistance(center.lat, center.lng, newLat, newLng);
  }

  return { lat: newLat, lng: newLng };
};

export function formatPriceUSD(price) {
  // If price is a string (e.g. "$120.00"), strip out anything except digits, dot and minus
  let amount = price;
  if (typeof amount === 'string') {
    const parsed = parseFloat(amount.replace(/[^0-9.-]+/g, ''));
    amount = isNaN(parsed) ? 0 : parsed;
  }

  // Format with no decimal places
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}
export const truncateWords = (text = '', wordLimit = 10) => {
  const words = text.split(/\s+/);
  return words.length > wordLimit ? words.slice(0, wordLimit).join(' ') + '…' : text;
};

/**
 * Sorts an array of options by the translated 'label' field using react-intl.
 * @param {Array<{ key: string, label: string }>} options - The array of options to sort.
 * @param {Object} intl - The intl object from react-intl.
 * @returns {Array<{ key: string, label: string }>} Sorted options by translated label.
 */
export const sortOptionsByLabel = (options) => {
  return [...options].sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * Recursively sorts categories and their subcategories by the 'name' field.
 * @param {Array<{ name: string, id: string, subcategories: any[] }>} categories - List of categories to sort.
 * @returns {Array} Sorted categories with sorted subcategories.
 */
export const sortCategoriesWithSubcategories = (categories) => {
  return [...categories]
    .map(category => ({
      ...category,
      subcategories: sortCategoriesWithSubcategories(category.subcategories || [])
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

export function getCategoriesByType(categoryData, type) {
  if (!type) return [];

  const hostCategories = categoryData.slice(0, 5); // First 5
  const providerCategories = categoryData.slice(5); // Rest

  return type === UserType.HOST ? hostCategories : providerCategories;
}

export const isCallForQuoteEnabled = callForQuote => {
  if (Array.isArray(callForQuote)) {
    return callForQuote[0] === 'true';
  } else if (typeof callForQuote === 'string') {
    return callForQuote === 'true';
  }
  return false;
};

export const getListingUserType = listing => {
  return listing?.attributes?.publicData?.userType || null;
};

export const getVerificationDetails = currentUser => {
  if (!currentUser && !currentUser?.id) {
    return null;
  }
  const identityId =
    currentUser?.attributes?.profile?.publicData?.verificationDetail?.identityId || null;
  const identityStatus =
    currentUser?.attributes?.profile?.publicData?.verificationDetail?.identityId || null;
  return { identityId, identityStatus };
};